import React from 'react';

import { useGetWhereSearch } from 'apis/handler/listing/useGetWhereSearch';
import { ReactComponent as SubwayIcon } from 'assets/icons/ic_subway.svg';
import { ReactComponent as TownIcon } from 'assets/icons/ic_town.svg';
import { ReactComponent as UnivIcon } from 'assets/icons/ic_univ.svg';
import LoadingSpinner from 'components/common/LoadingSpinner';
import { convertSubwayStationKorToEng } from 'shared/lib';
import { useWhereSearchStore } from 'store/useWhereSearchStore';

import WhereItem from './WhereItem';

interface WhereModalProps {
  originalValue: string;
  setOriginalValue: (value: string) => void;
  setWhereValue: (value: string) => void;
  setWhereType: (value: string) => void;
  onClose: () => void;
}

const WhereModal = ({ originalValue, setOriginalValue, setWhereValue, setWhereType, onClose }: WhereModalProps) => {
  const { whereSearches } = useWhereSearchStore();

  const { data: whereData, isLoading: whereIsLoading } = useGetWhereSearch(originalValue);

  const handleClick = ({ originalValue, value, type }: { originalValue: string; value: string; type: string }) => {
    setOriginalValue(originalValue);
    setWhereValue(value);
    setWhereType(type);
    onClose();
  };

  return (
    <div className="flex w-full flex-col gap-y-5 px-10 py-[30px]">
      <div className="flex w-full flex-col gap-y-[5px] border-b border-gray-30 pb-3">
        <h5 className="flex h-close items-center text-b3">Recently Searched</h5>
        <div className="flex w-full items-center gap-x-2">
          {whereSearches.length < 1 ? (
            <div className="text-c2 text-gray-50">No search history</div>
          ) : (
            whereSearches.map((item, index) => (
              <button
                key={index}
                className="flex shrink-0 items-center justify-center rounded-full border border-gray-40 px-2 py-1.5 text-c2 text-gray-40"
                onClick={() =>
                  handleClick({ originalValue: item.originalValue, value: item.whereValue, type: item.whereType })
                }
              >
                {item.originalValue}
              </button>
            ))
          )}
        </div>
      </div>
      <div className="flex w-full flex-col gap-y-4">
        <div className="flex w-full flex-col gap-y-1.25">
          <h5 className="text-b3">Top Universities</h5>
          <div className="flex w-full items-center gap-x-0.5 overflow-x-auto">
            {whereIsLoading ? (
              <LoadingSpinner isMini />
            ) : !whereData || whereData.universities.length === 0 ? (
              <div className="text-c2 text-gray-50">No matching data found.</div>
            ) : (
              whereData.universities.map((item, index) => (
                <WhereItem
                  key={index}
                  icon={UnivIcon}
                  text={item}
                  onClick={() => handleClick({ originalValue: item, value: item, type: 'UNIVERSITY' })}
                />
              ))
            )}
          </div>
        </div>
        <div className="flex w-full flex-col gap-y-1.25">
          <h5 className="text-b3">Top Towns</h5>
          <div className="flex w-full items-center gap-x-0.5 overflow-x-auto">
            {whereIsLoading ? (
              <LoadingSpinner isMini />
            ) : !whereData || whereData.towns.length === 0 ? (
              <div className="text-c2 text-gray-50">No matching data found.</div>
            ) : (
              whereData.towns.map((item, index) => (
                <WhereItem
                  key={index}
                  icon={TownIcon}
                  text={item}
                  onClick={() =>
                    handleClick({
                      originalValue: item,
                      value: item,
                      type: 'ADDRESS',
                    })
                  }
                />
              ))
            )}
          </div>
        </div>
        <div className="flex w-full flex-col gap-y-1.25">
          <h5 className="text-b3">Top Subway Stations</h5>
          <div className="flex w-full items-center gap-x-0.5 overflow-x-auto">
            {whereIsLoading ? (
              <LoadingSpinner isMini />
            ) : !whereData || whereData.subwayStations.length === 0 ? (
              <div className="text-c2 text-gray-50">No matching data found.</div>
            ) : (
              whereData.subwayStations.map((item, index) => (
                <WhereItem
                  key={index}
                  icon={SubwayIcon}
                  text={convertSubwayStationKorToEng(item)}
                  onClick={() =>
                    handleClick({
                      originalValue: convertSubwayStationKorToEng(item),
                      value: convertSubwayStationKorToEng(item),
                      type: 'SUBWAY_STATION',
                    })
                  }
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhereModal;
